import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, Sun } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PlaceSearch from "@/components/earthpulse/PlaceSearch";
import SocialProof from "@/components/earthpulse/SocialProof";
import SubscribedBadge from "@/components/earthpulse/SubscribedBadge";
import { createBirthChartSession, getSubscriberEmail } from "@/lib/api";
import useSubscription from "@/hooks/useSubscription";
import { SITE_URL } from "@/lib/site";
import { toast } from "sonner";

const inputCls =
  "bg-white border-slate-300 focus-visible:ring-[color:var(--gold)] focus-visible:border-[color:var(--gold)] rounded-lg h-11 text-slate-900 font-sans-body font-medium placeholder:text-slate-500 placeholder:font-normal";

export default function BirthChartForm() {
  const navigate = useNavigate();
  const { active: subActive, email: subEmail } = useSubscription();
  const [form, setForm] = useState({
    name: "",
    email: getSubscriberEmail() || "",
    birth_date: "",
    birth_time: "",
    latitude: "",
    longitude: "",
    place_label: "",
    promo_code: "",
  });
  const [skipEmail, setSkipEmail] = useState(false);
  const [loading, setLoading] = useState(false);

  // Once the hook resolves the stored subscriber email, hydrate the field.
  useEffect(() => {
    if (subEmail && !form.email) setForm((f) => ({ ...f, email: subEmail }));
  }, [subEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitLabel = subActive
    ? "Generate my reading · included"
    : "Continue — subscribe to unlock (£8.88 / mo)";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.birth_date) { toast.error("Birth date is required"); return; }
    if (!skipEmail) {
      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        toast.error("Enter a valid email — or tick “Show me on-screen only”");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name || null,
        email: skipEmail ? null : form.email,
        birth_date: form.birth_date,
        birth_time: form.birth_time || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        place_label: form.place_label || null,
        origin_url: SITE_URL,
        promo_code: form.promo_code ? form.promo_code.trim() : null,
      };
      const response = await createBirthChartSession(payload);
      const { checkout_url, promo_applied, needs_subscription, subscribe_url } = response;
      if (needs_subscription) {
        toast("Add a £8.88/month membership to unlock unlimited readings.",
          { description: "You'll be redirected in a moment." });
        try { localStorage.setItem("ep_subscriber_email", (form.email || "").trim().toLowerCase()); } catch { /* noop */ }
        setTimeout(() => { window.location.href = subscribe_url; }, 800);
        return;
      }
      if (promo_applied) {
        toast.success("Promo code applied — enjoy your free reading");
      }
      window.location.href = checkout_url;
    } catch (err) {
      toast.error("Could not start checkout", { description: err?.response?.data?.detail || "" });
      setLoading(false);
    }
  };

  // Suppress the browser's native invalid tooltip so our sonner toast can speak instead.
  const noNative = { onInvalid: (ev) => ev.preventDefault() };

  return (
    <div className="min-h-screen relative z-10">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/almanac" className="flex items-center gap-3 group" data-testid="back-to-dashboard">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Back to Guidance</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--gold)]" />
            <span className="font-serif-display text-white text-lg">Cosmic Birth Chart</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6 stagger-in">
            <div className="label-overline flex items-center gap-3">
              A Personal Reading <SubscribedBadge />
            </div>
            <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.02] tracking-tight">
              Where the sky <span className="font-serif-italic text-[color:var(--gold)]">held still</span> for you.
            </h1>
            <p className="mt-6 text-[color:var(--parchment-soft)] text-lg font-sans-body leading-relaxed max-w-md">
              Enter the moment you arrived. We&apos;ll calculate your Sun, Moon and Rising, then
              hand it to a warm literary voice for a reading — a small letter from the stars,
              yours to keep.
            </p>

            <div className="mt-10 space-y-4">
              {[
                subActive
                  ? { title: "Included with your membership.", body: "Generate as many readings as you like — no extra charge." }
                  : { title: "£8.88 / month · unlimited readings.", body: "One membership unlocks every Birth Chart & Compatibility reading. Cancel anytime." },
                { title: "Sun · Moon · Rising.", body: "Time & place unlock Moon and Rising; date alone gives you Sun." },
                { title: "Written by our narrative engine.", body: "Personality reading, gifts, shadows, and an affirmation." },
              ].map((x) => (
                <div key={x.title} className="flex items-start gap-3">
                  <Sun size={14} className="mt-1.5 text-[color:var(--gold)]" />
                  <div>
                    <div className="font-serif-display text-[color:var(--parchment)] text-base">{x.title}</div>
                    <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">{x.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <SocialProof />
            </div>
          </div>

          <form
            onSubmit={submit}
            noValidate
            className="md:col-span-6 parchment-card rounded-2xl p-8 md:p-10 stagger-in"
            data-testid="birth-chart-form"
            style={{ animationDelay: "80ms" }}
          >
            <div className="label-overline">Enter your details</div>
            <div className="mt-5 space-y-5">
              <div>
                <Label className="label-overline text-[color:var(--text-secondary)]">Name (optional)</Label>
                <Input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="What shall we call you?"
                  data-testid="input-name"
                />
              </div>

              {/* Gift-friendly framing — no toggle, just clear copy */}
              <div
                className="rounded-xl p-4 md:p-5"
                style={{ border: "1px solid rgba(229,168,124,0.35)", background: "rgba(229,168,124,0.05)" }}
                data-testid="gift-note"
              >
                <div className="flex items-start gap-3">
                  <div className="text-xl leading-none">🎁</div>
                  <div>
                    <div className="font-serif-display text-[color:var(--parchment)] text-lg leading-snug">
                      Buying this as a gift?
                    </div>
                    <p className="mt-1 font-serif-italic text-[color:var(--parchment-soft)] text-sm leading-relaxed">
                      Enter the recipient&apos;s birth details above — with your membership you can generate as many charts as you like, screenshot the beautifully-designed wheel and slip it into a card. No shipping, no waiting.
                    </p>
                  </div>
                </div>
              </div>



              <div>
                <Label className="label-overline text-[color:var(--text-secondary)]">Email address {skipEmail ? "" : "*"}</Label>
                <Input
                  className={inputCls}
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder={skipEmail ? "Not required — reading shown on screen" : "you@example.com"}
                  data-testid="input-email"
                  autoComplete="email"
                  disabled={skipEmail}
                  {...noNative}
                />
                <p className="mt-2 font-serif-italic text-[color:var(--text-muted)] text-xs">
                  {skipEmail
                    ? "You'll see the reading on screen right after payment — nothing sent by email."
                    : "Your reading is emailed here right after payment — no need to keep the page open."}
                </p>
                <label className="mt-3 flex items-center gap-2 text-[color:var(--text-secondary)] text-sm cursor-pointer select-none" data-testid="skip-email-label">
                  <input
                    type="checkbox"
                    checked={skipEmail}
                    onChange={(e) => setSkipEmail(e.target.checked)}
                    className="w-4 h-4 accent-[color:var(--gold)]"
                    data-testid="skip-email-checkbox"
                  />
                  <span className="font-serif-italic">Skip email · show me on screen only</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-overline text-[color:var(--text-secondary)]">Birth date *</Label>
                  <Input
                    className={inputCls}
                    type="date"
                    value={form.birth_date}
                    onChange={(e) => set("birth_date", e.target.value)}
                    data-testid="input-birth-date"
                    {...noNative}
                  />
                </div>
                <div>
                  <Label className="label-overline text-[color:var(--text-secondary)]">Birth time</Label>
                  <Input
                    className={inputCls}
                    type="time"
                    value={form.birth_time}
                    onChange={(e) => set("birth_time", e.target.value)}
                    data-testid="input-birth-time"
                  />
                </div>
              </div>

              <div>
                <Label className="label-overline text-[color:var(--text-secondary)]">Place of birth</Label>
                <PlaceSearch
                  value={form.place_label}
                  onChange={(v) => set("place_label", v)}
                  onSelect={(r) => setForm((f) => ({
                    ...f,
                    place_label: r.label,
                    latitude: r.lat.toFixed(4),
                    longitude: r.lng.toFixed(4),
                  }))}
                  testid="input-place"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-overline text-[color:var(--text-secondary)]">Latitude</Label>
                  <Input
                    className={inputCls}
                    type="number"
                    step="0.0001"
                    value={form.latitude}
                    onChange={(e) => set("latitude", e.target.value)}
                    placeholder="Auto-filled from place"
                    data-testid="input-latitude"
                    readOnly={!!form.place_label && !!form.latitude}
                  />
                </div>
                <div>
                  <Label className="label-overline text-[color:var(--text-secondary)]">Longitude</Label>
                  <Input
                    className={inputCls}
                    type="number"
                    step="0.0001"
                    value={form.longitude}
                    onChange={(e) => set("longitude", e.target.value)}
                    placeholder="Auto-filled from place"
                    data-testid="input-longitude"
                    readOnly={!!form.place_label && !!form.longitude}
                  />
                </div>
              </div>
              <p className="font-serif-italic text-[color:var(--text-muted)] text-xs">
                Choose your birthplace from the dropdown — we&apos;ll fill the coordinates for you.
                Lat/long unlock your Rising sign.
              </p>

              <div className="pt-2">
                <Label className="label-overline text-[color:var(--text-secondary)]">Promo code</Label>
                <Input
                  className={inputCls}
                  type="text"
                  value={form.promo_code}
                  onChange={(e) => set("promo_code", e.target.value)}
                  placeholder="Optional — e.g. ABC123"
                  data-testid="input-promo-code"
                  autoCapitalize="characters"
                  autoComplete="off"
                />
                <p className="mt-2 font-serif-italic text-[color:var(--text-muted)] text-xs">
                  A valid promo code skips payment — you go straight to your reading.
                </p>
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-sans-body tracking-wide"
                  data-testid="submit-birth-chart"
                >
                  {loading ? "Preparing your chart…" : submitLabel}
                </Button>
                <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
                  {subActive
                    ? "Included with your Earth Pulse membership · unlimited readings."
                    : "Membership managed by Stripe. Cancel any time from the billing portal."}
                </p>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
